import { type FormEvent, useEffect, useState } from 'react';
import {
  type ChecklistItem,
  type CommunityJurisdiction,
  KNOWLEDGE_AUTHORITIES,
  KNOWLEDGE_CATEGORIES,
  type KnowledgeAuthority,
  type KnowledgeCategory,
  type KnowledgeSource,
  type KnowledgeSourceDetail,
  PROJECT_TYPES,
  type ProjectType,
} from '@shared/types';
import { ArchNav } from '../components/ArchNav';
import { Freshness, SourceBadge } from '../components/KnowledgeAnswer';
import { PageHeader } from '../components/PageHeader';
import { Banner, EmptyState, Field, LoadingPage, Spinner, Tag } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTimeLabel } from '../lib/format';
import { useToast } from '../lib/toast';
import { useAsync } from '../lib/useAsync';

const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'bad' | 'info' | 'pend' | 'neutral' }> = {
  indexed: { label: 'Indexed', tone: 'ok' },
  link_only: { label: 'Link only', tone: 'pend' },
  sync_pending: { label: 'Sync pending', tone: 'info' },
  failed: { label: 'Failed', tone: 'bad' },
  needs_review: { label: 'Needs review', tone: 'warn' },
  archived: { label: 'Archived', tone: 'neutral' },
};

const FLAG_COPY: Record<string, string> = {
  'ignore-instructions': 'text telling an AI to ignore its instructions',
  'role-change': 'text trying to change an AI’s role',
  'fake-system': 'text posing as system instructions',
  'fake-tag': 'hidden instruction tags',
  exfiltration: 'text asking an AI to reveal secrets',
  'action-request': 'text asking for automatic approvals',
};

const EMPTY_FORM = {
  title: '',
  url: '',
  authority: 'city' as KnowledgeAuthority,
  authorityName: '',
  category: 'general' as KnowledgeCategory,
  description: '',
  jurisdiction: '',
  tags: '',
  visibility: 'owners' as 'owners' | 'committee',
  official: false,
  ingestionMode: 'ingest' as 'ingest' | 'link_only' | 'dataset',
  crawlDepth: 0,
  maxPages: 1,
  syncCadence: 'weekly' as 'manual' | 'daily' | 'weekly' | 'monthly',
  effectiveDate: '',
};

function confirmAction(message: string): boolean {
  return window.confirm(message);
}

export function KnowledgeSourcesPage() {
  const { user, isBoard, isSuperAdmin } = useAuth();
  const { notify, fail } = useToast();
  const sources = useAsync(() => api.archSources(), [user?.communityId]);
  const jurisdiction = useAsync(() => api.archJurisdiction(), [user?.communityId]);
  const [filter, setFilter] = useState<string>('all');
  const [panel, setPanel] = useState<'none' | 'add' | 'bulk'>('none');
  const [openId, setOpenId] = useState<string | null>(null);

  const list = sources.data?.sources ?? [];
  const pending = list.some((source) => source.status === 'sync_pending');

  // While a sync runs, refresh so the card moves to indexed / needs review / failed.
  useEffect(() => {
    if (!pending) return;
    const timer = window.setInterval(() => void sources.reload(), 4000);
    return () => window.clearInterval(timer);
  }, [pending, sources]);

  if (sources.loading && !sources.data) return <LoadingPage label="Loading knowledge sources…" />;

  const apply = (next: KnowledgeSource[]) => sources.setData({ sources: next });
  async function run<T>(label: string, action: () => Promise<{ sources: KnowledgeSource[] } & T>, success?: string) {
    try {
      const result = await action();
      apply(result.sources);
      if (success) notify(success, 'success');
      return result;
    } catch (error) {
      fail(error, label);
      return null;
    }
  }

  const counts = list.reduce<Record<string, number>>((acc, source) => {
    const key = source.stale ? 'stale' : source.status;
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
  const visible = list.filter((source) =>
    filter === 'all' ? true : filter === 'stale' ? source.stale : source.status === filter,
  );
  const place = jurisdiction.data?.jurisdiction;
  const looksAustin = /austin/i.test(place?.municipality ?? '');

  return (
    <div>
      <PageHeader
        title="Knowledge Sources"
        lede="Official city, county and HOA resources the assistant can cite. Nothing reaches homeowners until you review and publish it. Board and Architecture Review Board only."
      />
      <ArchNav />

      <JurisdictionCard
        value={place ?? null}
        canEdit={isBoard || isSuperAdmin}
        onSaved={(next) => jurisdiction.setData({ jurisdiction: next })}
      />

      {list.length === 0 && (
        <div className="card">
          <h3>Getting started</h3>
          <ol>
            <li>Confirm your community’s city and county above. A mailing address in a city does not mean the lot is inside its limits.</li>
            <li>Add official pages (zoning, permits, ADUs, fences…) or upload HOA documents on the Ask AI page.</li>
            <li>Each page is fetched safely and saved as a version. Review the extracted text, then publish it.</li>
            <li>Interactive tools such as property lookups stay as links; the assistant points people to them.</li>
          </ol>
        </div>
      )}

      <div className="btn-row">
        <button type="button" className="btn" onClick={() => setPanel(panel === 'add' ? 'none' : 'add')}>
          {panel === 'add' ? 'Close' : 'Add source'}
        </button>
        <button type="button" className="btn s" onClick={() => setPanel(panel === 'bulk' ? 'none' : 'bulk')}>
          {panel === 'bulk' ? 'Close' : 'Bulk import URLs'}
        </button>
        {(looksAustin || list.length === 0) && (
          <button
            type="button"
            className="btn s"
            onClick={() => {
              if (
                !confirmAction(
                  'Add three City of Austin starter sources (ADUs, Property Profile lookup, Short-Term Rentals)? They are added unpublished and unverified. Only use them if your community is inside Austin city limits.',
                )
              )
                return;
              void run('The starter sources were not added.', () => api.archAustinStarter()).then((result) => {
                if (result) notify(result.added ? `Added ${result.added} Austin starter sources. Review each before publishing.` : 'The Austin starter sources are already here.');
              });
            }}
          >
            Add Austin starter sources
          </button>
        )}
      </div>

      {panel === 'add' && (
        <SourceForm
          onCancel={() => setPanel('none')}
          onSubmit={async (input) => {
            const ok = await run('That source was not added.', () => api.archCreateSource(input), 'Source added. It is being fetched now.');
            if (ok) setPanel('none');
          }}
        />
      )}
      {panel === 'bulk' && <BulkImport onDone={(next) => apply(next)} />}

      {list.length > 0 && (
        <div className="chip-row" role="radiogroup" aria-label="Filter by status">
          {['all', 'needs_review', 'sync_pending', 'indexed', 'link_only', 'failed', 'stale'].map((key) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={filter === key}
              className={`chip${filter === key ? ' on' : ''}`}
              onClick={() => setFilter(key)}
            >
              {key === 'all' ? 'All' : key === 'stale' ? 'Stale' : STATUS[key].label}
              {key !== 'all' && counts[key] ? ` (${counts[key]})` : ''}
            </button>
          ))}
        </div>
      )}

      {list.length > 0 && visible.length === 0 && <EmptyState title="No sources with that status." />}

      <div className="kc-grid">
        {visible.map((source) => (
          <article key={source.id} className="card kc-card">
            <div className="tag-row">
              <Tag tone={STATUS[source.status]?.tone ?? 'neutral'}>{STATUS[source.status]?.label ?? source.status}</Tag>
              {source.stale && <Tag tone="warn">Stale</Tag>}
              <SourceBadge section={source.authority === 'hoa' ? 'hoa' : 'municipal'} official={source.official} authorityName={source.authorityName} />
              {source.published ? <Tag tone="ok">Published</Tag> : <Tag>Not published</Tag>}
              {source.visibility === 'committee' && <Tag tone="warn">Committee only</Tag>}
            </div>
            <h3>{source.title}</h3>
            <div className="mut">
              {KNOWLEDGE_CATEGORIES[source.category]} · {source.authorityName || KNOWLEDGE_AUTHORITIES[source.authority]}
              {source.publishedVersion ? ` · live version ${source.publishedVersion}` : ''}
            </div>
            <a href={source.url} target="_blank" rel="noopener noreferrer" className="mut" style={{ wordBreak: 'break-all' }}>
              {source.url}
            </a>
            {source.pendingVersion && (
              <Banner kind="warn">
                Version {source.pendingVersion.version} is waiting for review
                {source.pendingVersion.change
                  ? source.pendingVersion.change.substantive
                    ? ' — the page changed substantively.'
                    : ' — minor changes.'
                  : '.'}
              </Banner>
            )}
            {source.lastError && <Banner kind="error">Last sync failed: {source.lastError}</Banner>}
            {source.linkOnlyReason && <p className="mut" style={{ margin: 0 }}>{source.linkOnlyReason}</p>}
            <Freshness checkedAt={source.lastCheckedAt} stale={source.stale} />
            <div className="kc-card-actions">
              <button type="button" className="btn s tiny" onClick={() => setOpenId(openId === source.id ? null : source.id)} aria-expanded={openId === source.id}>
                {openId === source.id ? 'Hide details' : 'Details'}
              </button>
              {!source.archived && (
                <button
                  type="button"
                  className="btn s tiny"
                  onClick={() => {
                    if (confirmAction(`Fetch “${source.title}” again now?`)) {
                      void run('Reindex was not started.', () => api.archSyncSource(source.id), 'Reindex started.');
                    }
                  }}
                >
                  Reindex
                </button>
              )}
              {!source.archived && (
                <button
                  type="button"
                  className="btn s tiny"
                  onClick={() => {
                    const next = !source.published;
                    const message = next
                      ? `Publish “${source.title}”? ${source.visibility === 'committee' ? 'Committee members' : 'Homeowners and the committee'} will see it and the assistant can cite it.`
                      : `Unpublish “${source.title}”? The assistant will stop citing it.`;
                    if (confirmAction(message)) {
                      void run('That change was not saved.', () => api.archPublishSource(source.id, next), next ? 'Published.' : 'Unpublished.');
                    }
                  }}
                >
                  {source.published ? 'Unpublish' : 'Publish'}
                </button>
              )}
              <button
                type="button"
                className="btn ghost tiny"
                onClick={() => {
                  const next = !source.archived;
                  if (confirmAction(next ? `Archive “${source.title}”? It is unpublished and no longer synced. Its history is kept.` : `Restore “${source.title}”?`)) {
                    void run('That change was not saved.', () => api.archArchiveSource(source.id, next), next ? 'Archived.' : 'Restored.');
                  }
                }}
              >
                {source.archived ? 'Restore' : 'Archive'}
              </button>
            </div>
            {openId === source.id && <SourceDetails id={source.id} onChange={apply} />}
          </article>
        ))}
      </div>

      <ChecklistEditor />
    </div>
  );
}

/* ------------------------------------------------------------ jurisdiction */

function JurisdictionCard({
  value,
  canEdit,
  onSaved,
}: {
  value: CommunityJurisdiction | null;
  canEdit: boolean;
  onSaved: (next: CommunityJurisdiction) => void;
}) {
  const { notify, fail } = useToast();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ municipality: '', county: '', stateRegion: '', country: 'US', timezone: '', boundaryRef: '', notes: '', verified: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!value) return;
    setForm({
      municipality: value.municipality ?? '',
      county: value.county ?? '',
      stateRegion: value.stateRegion ?? '',
      country: value.country,
      timezone: value.timezone ?? '',
      boundaryRef: value.boundaryRef ?? '',
      notes: value.notes ?? '',
      verified: Boolean(value.verifiedAt),
    });
  }, [value]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { jurisdiction } = await api.archSaveJurisdiction(form);
      onSaved(jurisdiction);
      setEditing(false);
      notify('Jurisdiction saved.', 'success');
    } catch (error) {
      fail(error, 'The jurisdiction was not saved.');
    } finally {
      setBusy(false);
    }
  }

  const place = value ? [value.municipality, value.county, value.stateRegion, value.country].filter(Boolean).join(', ') : '';
  return (
    <div className="card">
      <div className="card-head">
        <h3>Community jurisdiction</h3>
        {value?.verifiedAt ? <Tag tone="ok">Verified by {value.verifiedByName ?? 'the board'}</Tag> : <Tag tone="pend">Not verified</Tag>}
      </div>
      {!editing ? (
        <>
          <p style={{ margin: 0 }}>{place || 'Not set yet.'}</p>
          {value?.mailingAddress && <p className="mut">Mailing address on file: {value.mailingAddress}</p>}
          {canEdit && (
            <div className="btn-row">
              <button type="button" className="btn s tiny" onClick={() => setEditing(true)}>
                Edit jurisdiction
              </button>
            </div>
          )}
        </>
      ) : (
        <form onSubmit={save}>
          <div className="kc-form-grid">
            <Field label="City / municipality" hint="The authority that issues building permits for the lots.">
              <input value={form.municipality} onChange={(e) => setForm({ ...form, municipality: e.target.value })} placeholder="City of Austin" />
            </Field>
            <Field label="County">
              <input value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} placeholder="Travis County" />
            </Field>
            <Field label="State">
              <input value={form.stateRegion} onChange={(e) => setForm({ ...form, stateRegion: e.target.value })} placeholder="TX" />
            </Field>
            <Field label="Country">
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </Field>
            <Field label="Time zone">
              <input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="America/Chicago" />
            </Field>
            <Field label="Boundary reference" hint="Optional: plat, subdivision or GIS id.">
              <input value={form.boundaryRef} onChange={(e) => setForm({ ...form, boundaryRef: e.target.value })} />
            </Field>
          </div>
          <Field label="Notes" hint="E.g. some lots are in the county ETJ, not city limits.">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <label className="kc-check">
            <input type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} />
            <span>I have checked that the community’s lots are inside this jurisdiction (not just the mailing city).</span>
          </label>
          <div className="btn-row">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? 'Saving…' : 'Save jurisdiction'}
            </button>
            <button type="button" className="btn s" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- add forms */

function SourceForm({ onSubmit, onCancel }: { onSubmit: (input: Record<string, unknown>) => Promise<void>; onCancel: () => void }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<typeof EMPTY_FORM>) => setForm({ ...form, ...patch });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    await onSubmit({
      ...form,
      tags: form.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      effectiveDate: form.effectiveDate || null,
    });
    setBusy(false);
  }

  return (
    <form className="card" onSubmit={submit}>
      <h3>Add a source</h3>
      <div className="kc-form-grid">
        <Field label="Title">
          <input required maxLength={200} value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="City of Austin — Fences" />
        </Field>
        <Field label="Official web address" hint="https only for public sites. The page is fetched from our servers, not your browser.">
          <input required type="url" value={form.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://" />
        </Field>
        <Field label="Authority">
          <select value={form.authority} onChange={(e) => set({ authority: e.target.value as KnowledgeAuthority })}>
            {Object.entries(KNOWLEDGE_AUTHORITIES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Authority name">
          <input value={form.authorityName} onChange={(e) => set({ authorityName: e.target.value })} placeholder="City of Austin" />
        </Field>
        <Field label="Category">
          <select value={form.category} onChange={(e) => set({ category: e.target.value as KnowledgeCategory })}>
            {Object.entries(KNOWLEDGE_CATEGORIES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="How to use it">
          <select value={form.ingestionMode} onChange={(e) => set({ ingestionMode: e.target.value as typeof form.ingestionMode })}>
            <option value="ingest">Read and index the page</option>
            <option value="link_only">Link only (interactive tool or lookup)</option>
            <option value="dataset">Official dataset or API (JSON/CSV)</option>
          </select>
        </Field>
        <Field label="Who can see it">
          <select value={form.visibility} onChange={(e) => set({ visibility: e.target.value as 'owners' | 'committee' })}>
            <option value="owners">Homeowners and the committee</option>
            <option value="committee">Committee only</option>
          </select>
        </Field>
        <Field label="Check for changes">
          <select value={form.syncCadence} onChange={(e) => set({ syncCadence: e.target.value as typeof form.syncCadence })}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="manual">Only when I reindex</option>
          </select>
        </Field>
        {form.ingestionMode === 'ingest' && (
          <>
            <Field label="Also read linked pages" hint="Same site and folder only.">
              <select value={form.crawlDepth} onChange={(e) => set({ crawlDepth: Number(e.target.value), maxPages: Number(e.target.value) ? Math.max(form.maxPages, 5) : 1 })}>
                <option value={0}>No, this page only</option>
                <option value={1}>One level of links</option>
                <option value={2}>Two levels of links</option>
              </select>
            </Field>
            {form.crawlDepth > 0 && (
              <Field label="Most pages to read">
                <input type="number" min={1} max={20} value={form.maxPages} onChange={(e) => set({ maxPages: Math.min(20, Math.max(1, Number(e.target.value) || 1)) })} />
              </Field>
            )}
          </>
        )}
        <Field label="Effective date" hint="Optional, if the source states one.">
          <input type="date" value={form.effectiveDate} onChange={(e) => set({ effectiveDate: e.target.value })} />
        </Field>
        <Field label="Tags" hint="Comma separated.">
          <input value={form.tags} onChange={(e) => set({ tags: e.target.value })} placeholder="fence, height" />
        </Field>
      </div>
      <Field label="Description">
        <textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} />
      </Field>
      <label className="kc-check">
        <input type="checkbox" checked={form.official} onChange={(e) => set({ official: e.target.checked })} />
        <span>I have checked this is the authority’s official site and that it applies to our community.</span>
      </label>
      <div className="btn-row">
        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Adding…' : 'Add and fetch'}
        </button>
        <button type="button" className="btn s" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function BulkImport({ onDone }: { onDone: (sources: KnowledgeSource[]) => void }) {
  const { fail } = useToast();
  const [text, setText] = useState('');
  const [defaults, setDefaults] = useState({ authority: 'city' as KnowledgeAuthority, authorityName: '', category: 'general' as KnowledgeCategory, visibility: 'owners' as 'owners' | 'committee' });
  const [results, setResults] = useState<{ url: string; ok: boolean; error?: string }[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const urls = text.split(/\s+/).map((line) => line.trim()).filter(Boolean).slice(0, 25);
    if (urls.length === 0) return;
    setBusy(true);
    try {
      const result = await api.archBulkSources(urls, defaults);
      setResults(result.results);
      onDone(result.sources);
    } catch (error) {
      fail(error, 'Those addresses were not imported.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h3>Bulk import</h3>
      <p className="mut">One web address per line, up to 25. Each is checked on its own and added unpublished. Upload HOA files on the Ask AI page.</p>
      <Field label="Web addresses">
        <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={'https://www.example.gov/permits\nhttps://www.example.gov/zoning'} />
      </Field>
      <div className="kc-form-grid">
        <Field label="Authority">
          <select value={defaults.authority} onChange={(e) => setDefaults({ ...defaults, authority: e.target.value as KnowledgeAuthority })}>
            {Object.entries(KNOWLEDGE_AUTHORITIES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Authority name">
          <input value={defaults.authorityName} onChange={(e) => setDefaults({ ...defaults, authorityName: e.target.value })} />
        </Field>
        <Field label="Category">
          <select value={defaults.category} onChange={(e) => setDefaults({ ...defaults, category: e.target.value as KnowledgeCategory })}>
            {Object.entries(KNOWLEDGE_CATEGORIES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Who can see them">
          <select value={defaults.visibility} onChange={(e) => setDefaults({ ...defaults, visibility: e.target.value as 'owners' | 'committee' })}>
            <option value="owners">Homeowners and the committee</option>
            <option value="committee">Committee only</option>
          </select>
        </Field>
      </div>
      <div className="btn-row">
        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Importing…' : 'Import'}
        </button>
      </div>
      {results && (
        <ul aria-live="polite">
          {results.map((item) => (
            <li key={item.url}>
              {item.ok ? <Tag tone="ok">Added</Tag> : <Tag tone="bad">Skipped</Tag>} {item.url}
              {item.error ? <span className="mut"> — {item.error}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}

/* ----------------------------------------------------------------- details */

function SourceDetails({ id, onChange }: { id: string; onChange: (sources: KnowledgeSource[]) => void }) {
  const { notify, fail } = useToast();
  const detail = useAsync(() => api.archSource(id), [id]);
  const [preview, setPreview] = useState<{ versionId: string; text: string; truncated: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  if (detail.loading && !detail.data) return <Spinner label="Loading details" />;
  const data: KnowledgeSourceDetail | null = detail.data;
  if (!data) return null;

  async function act(action: () => Promise<{ sources: KnowledgeSource[] }>, message: string) {
    setBusy(true);
    try {
      const result = await action();
      onChange(result.sources);
      await detail.reload();
      notify(message, 'success');
    } catch (error) {
      fail(error, 'That change was not saved.');
    } finally {
      setBusy(false);
    }
  }

  async function showPreview(versionId: string) {
    try {
      const result = await api.archSourceVersion(id, versionId);
      setPreview({ versionId, text: result.text, truncated: result.truncated });
    } catch (error) {
      fail(error, 'Could not load that version.');
    }
  }

  const source = data.source;
  return (
    <div className="kc-research">
      <SourceSettings source={source} onSaved={(next) => {
        onChange(next);
        void detail.reload();
      }} />

      <h4>Versions</h4>
      {data.versions.length === 0 ? (
        <p className="mut">{source.ingestionMode === 'link_only' || source.status === 'link_only' ? 'Link-only sources have no indexed text.' : 'Nothing fetched yet.'}</p>
      ) : (
        <ul className="kc-cites">
          {data.versions.map((version) => (
            <li key={version.id} className="kc-cite">
              <div className="kc-cite-head">
                <strong>Version {version.version}</strong>
                <Tag tone={version.status === 'published' ? 'ok' : version.status === 'pending_review' ? 'warn' : 'neutral'}>
                  {version.status.replace('_', ' ')}
                </Tag>
              </div>
              <div className="mut">
                Fetched {dateTimeLabel(version.fetchedAt)} · {version.chars.toLocaleString()} characters · {version.pages.length} page
                {version.pages.length === 1 ? '' : 's'}
                {version.change ? ` · ${Math.round(version.change.ratio * 100)}% changed${version.change.substantive ? ' (substantive)' : ''}` : ''}
                {version.reviewedByName ? ` · reviewed by ${version.reviewedByName}` : ''}
              </div>
              {version.flags.length > 0 && (
                <Banner kind="warn">
                  Removed before indexing: {version.flags.map((flag) => FLAG_COPY[flag] ?? flag).join(', ')}. Check this page is genuine.
                </Banner>
              )}
              <div className="btn-row" style={{ marginTop: 6 }}>
                <button type="button" className="btn s tiny" onClick={() => void showPreview(version.id)}>
                  Preview text
                </button>
                {version.status === 'pending_review' && (
                  <>
                    <button
                      type="button"
                      className="btn tiny"
                      disabled={busy}
                      onClick={() => {
                        if (confirmAction(`Approve and publish version ${version.version}? ${source.visibility === 'committee' ? 'The committee' : 'Homeowners and the committee'} will be able to search it right away.`)) {
                          void act(() => api.archApproveVersion(id, version.id), `Version ${version.version} published.`);
                        }
                      }}
                    >
                      Approve and publish
                    </button>
                    <button
                      type="button"
                      className="btn ghost tiny"
                      disabled={busy}
                      onClick={() => {
                        if (confirmAction(`Reject version ${version.version}? The currently published version (if any) stays live.`)) {
                          void act(() => api.archRejectVersion(id, version.id), 'Version rejected.');
                        }
                      }}
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
              {preview?.versionId === version.id && (
                <div className="kc-preview" tabIndex={0} aria-label={`Extracted text of version ${version.version}`}>
                  {preview.text}
                  {preview.truncated ? '\n\n[Preview shortened]' : ''}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <h4 style={{ marginTop: 12 }}>Sync history</h4>
      {data.jobs.length === 0 ? (
        <p className="mut">No syncs yet.</p>
      ) : (
        <ul>
          {data.jobs.map((job) => (
            <li key={job.id}>
              {dateTimeLabel(job.createdAt)} — {job.status}
              {job.outcome ? ` (${job.outcome.replace('_', ' ')})` : ''}
              {job.attempt > 1 ? `, attempt ${job.attempt}` : ''}
              {job.error ? <span className="mut"> — {job.error}</span> : null}
            </li>
          ))}
        </ul>
      )}

      <h4 style={{ marginTop: 12 }}>Change history</h4>
      {data.history.length === 0 ? (
        <p className="mut">No changes recorded.</p>
      ) : (
        <ul>
          {data.history.map((entry, index) => (
            <li key={`${entry.createdAt}-${index}`}>
              {dateTimeLabel(entry.createdAt)} — {entry.action.replace('arch_source.', '').replace(/_/g, ' ')}
              {entry.actorName ? ` by ${entry.actorName}` : ' (automatic)'}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SourceSettings({ source, onSaved }: { source: KnowledgeSource; onSaved: (sources: KnowledgeSource[]) => void }) {
  const { notify, fail } = useToast();
  const [form, setForm] = useState({
    visibility: source.visibility,
    official: source.official,
    category: source.category,
    syncCadence: source.syncCadence,
    priority: source.priority,
  });
  const [busy, setBusy] = useState(false);
  const changed =
    form.visibility !== source.visibility ||
    form.official !== source.official ||
    form.category !== source.category ||
    form.syncCadence !== source.syncCadence ||
    form.priority !== source.priority;

  async function save() {
    setBusy(true);
    try {
      const result = await api.archUpdateSource(source.id, form);
      onSaved(result.sources);
      notify('Source updated.', 'success');
    } catch (error) {
      fail(error, 'The source was not updated.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h4>Settings</h4>
      <div className="kc-form-grid">
        <Field label="Who can see it">
          <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value as 'owners' | 'committee' })}>
            <option value="owners">Homeowners and the committee</option>
            <option value="committee">Committee only</option>
          </select>
        </Field>
        <Field label="Category">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as KnowledgeCategory })}>
            {Object.entries(KNOWLEDGE_CATEGORIES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Check for changes">
          <select value={form.syncCadence} onChange={(e) => setForm({ ...form, syncCadence: e.target.value as typeof form.syncCadence })}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="manual">Only when I reindex</option>
          </select>
        </Field>
        <Field label="Priority" hint="Higher is listed and searched first.">
          <input type="number" min={0} max={100} value={form.priority} onChange={(e) => setForm({ ...form, priority: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} />
        </Field>
      </div>
      <label className="kc-check">
        <input type="checkbox" checked={form.official} onChange={(e) => setForm({ ...form, official: e.target.checked })} />
        <span>Verified official source for this community</span>
      </label>
      {changed && (
        <div className="btn-row">
          <button type="button" className="btn tiny" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------- checklists */

function ChecklistEditor() {
  const { notify, fail } = useToast();
  const [projectType, setProjectType] = useState<ProjectType>('Fence');
  const checklist = useAsync(() => api.archChecklist(projectType), [projectType]);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (checklist.data) setItems(checklist.data.items);
  }, [checklist.data]);

  async function save(next: ChecklistItem[]) {
    setBusy(true);
    try {
      const result = await api.archSaveChecklist(projectType, next);
      checklist.setData(result);
      notify(next.length ? 'Checklist saved.' : 'Checklist reset to the default.', 'success');
    } catch (error) {
      fail(error, 'The checklist was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Request checklists</h3>
      <p className="mut">
        Shown to homeowners before they submit. Mark an item required only when your governing documents require it.
      </p>
      <Field label="Project type">
        <select value={projectType} onChange={(e) => setProjectType(e.target.value as ProjectType)}>
          {PROJECT_TYPES.map((type) => (
            <option key={type}>{type}</option>
          ))}
        </select>
      </Field>
      {checklist.data && !checklist.data.custom && <p className="mut">Using the default list. Save to customize it.</p>}
      <ul className="kc-checklist">
        {items.map((item, index) => (
          <li key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              aria-label={`Checklist item ${index + 1}`}
              value={item.label}
              onChange={(e) => setItems(items.map((other, i) => (i === index ? { ...other, label: e.target.value } : other)))}
            />
            <label className="kc-check" style={{ margin: 0, whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                aria-label={`Required: ${item.label || `item ${index + 1}`}`}
                checked={item.required}
                onChange={(e) => setItems(items.map((other, i) => (i === index ? { ...other, required: e.target.checked } : other)))}
              />
              <span>Required</span>
            </label>
            <button type="button" className="btn ghost tiny" aria-label={`Remove ${item.label}`} onClick={() => setItems(items.filter((_, i) => i !== index))}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="btn-row">
        <button
          type="button"
          className="btn s tiny"
          onClick={() => setItems([...items, { id: `item-${Date.now()}`, label: '', required: false }])}
        >
          Add item
        </button>
        <button type="button" className="btn tiny" disabled={busy} onClick={() => void save(items.filter((item) => item.label.trim()))}>
          Save checklist
        </button>
        {checklist.data?.custom && (
          <button type="button" className="btn ghost tiny" disabled={busy} onClick={() => void save([])}>
            Reset to default
          </button>
        )}
      </div>
    </div>
  );
}
